"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import type { Product } from "@/lib/types";

type Props = {
  product: Product;
};

export default function ProductMeta({ product }: Props) {
  const sizes = product.options.find((o) => o.name === "Size")?.values ?? [];
  const finishes = product.options.find((o) => o.name === "Finish")?.values ?? [];

  const sections: { id: string; title: string; content: React.ReactNode }[] = [
    {
      id: "specification",
      title: "Specification",
      content: (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
          <div className="flex justify-between border-b border-[color:var(--color-border)] pb-2">
            <dt className="text-[color:var(--color-muted)]">Type</dt>
            <dd className="font-medium">{product.productType}</dd>
          </div>
          <div className="flex justify-between border-b border-[color:var(--color-border)] pb-2">
            <dt className="text-[color:var(--color-muted)]">Vendor</dt>
            <dd className="font-medium">{product.vendor}</dd>
          </div>
          {sizes.length > 0 && (
            <div className="flex justify-between border-b border-[color:var(--color-border)] pb-2">
              <dt className="text-[color:var(--color-muted)]">Sizes</dt>
              <dd className="font-medium text-right">{sizes.join(", ")}</dd>
            </div>
          )}
          {finishes.length > 0 && (
            <div className="flex justify-between border-b border-[color:var(--color-border)] pb-2">
              <dt className="text-[color:var(--color-muted)]">Finishes</dt>
              <dd className="font-medium text-right">{finishes.join(", ")}</dd>
            </div>
          )}
          <div className="flex justify-between border-b border-[color:var(--color-border)] pb-2">
            <dt className="text-[color:var(--color-muted)]">Origin</dt>
            <dd className="font-medium">Italy / UK</dd>
          </div>
          <div className="flex justify-between border-b border-[color:var(--color-border)] pb-2">
            <dt className="text-[color:var(--color-muted)]">Slip rating</dt>
            <dd className="font-medium">R10 (matt)</dd>
          </div>
        </dl>
      ),
    },
    {
      id: "delivery",
      title: "Delivery & returns",
      content: (
        <div className="space-y-3 text-sm text-[color:var(--color-text)]/90">
          <p>
            Pallet delivery UK-wide within 3–5 working days. Free on orders over £600.
            Smaller orders ship on next-day courier.
          </p>
          <p>
            30-day returns on full boxes. Opened boxes may be exchanged for samples.
          </p>
        </div>
      ),
    },
    {
      id: "care",
      title: "Installation & care",
      content: (
        <div className="space-y-3 text-sm text-[color:var(--color-text)]/90">
          <p>
            Lay with a flexible polymer-modified adhesive on a primed, level substrate.
            Always mix boxes to balance natural variation. Order 10% extra for cuts and wastage.
          </p>
          <p>
            Daily care: a soft mop with a neutral pH floor cleaner. Avoid acidic cleaners on
            marble and natural stone.
          </p>
        </div>
      ),
    },
  ];

  const [open, setOpen] = useState<string | null>("specification");

  return (
    <div className="border-t border-[color:var(--color-border)] mt-10">
      <ul>
        {sections.map((s) => {
          const isOpen = open === s.id;
          return (
            <li key={s.id} className="border-b border-[color:var(--color-border)]">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : s.id)}
                className="w-full flex items-center justify-between py-5 text-left text-sm font-medium hover:text-[color:var(--color-primary)]"
                aria-expanded={isOpen}
              >
                <span className="text-base">{s.title}</span>
                <ChevronDown
                  size={18}
                  className={cn(
                    "transition-transform text-[color:var(--color-muted)]",
                    isOpen && "rotate-180 text-[color:var(--color-primary)]",
                  )}
                />
              </button>
              {isOpen && <div className="pb-6 -mt-1">{s.content}</div>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
